import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import {
  fetchLeads,
  fetchLeadsByDate,
  fetchLeadsByDealStatus,
  fetchLeadStats,
} from "../services/leadService.js";
import { fetchUsersByRole } from "../services/teamService.js";
import { getCurrentUser } from "../services/authService.js";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import EditLeadModal from "./EditLeadModal.jsx";
import DashboardHeader from "../components/dashboard/DashboardHeader.jsx";
import DashboardStats from "../components/dashboard/DashboardStats.jsx";
import DashboardFilterCalendar from "../components/dashboard/DashboardFilterCalendar.jsx";
import DashboardLeadCard from "../components/dashboard/DashboardLeadCard.jsx";

const DASH_CACHE_KEY = "dashboard_cache";
const DASH_SCROLL_KEY = "dashboard_scroll";
const CACHE_TTL_MS = 2 * 60 * 1000;

function saveCache(data) {
  try {
    sessionStorage.setItem(
      DASH_CACHE_KEY,
      JSON.stringify({ ...data, ts: Date.now() }),
    );
  } catch {}
}

const DEAL_STATUS_LABELS = {
  new: "New",
  call_back: "Call Back",
  meeting: "Meeting",
  quotation_created: "Quotation Created",
  quotation_send: "Quotation Sent",
  quotation_revised: "Quotation Revised",
  won: "Won",
  lost: "Lost",
};

function loadCache() {
  try {
    const raw = sessionStorage.getItem(DASH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(DASH_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function pollAndScroll(id, onDone, retries = 30, interval = 50) {
  const poll = (remaining) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "instant", block: "center" });
      onDone?.();
      return;
    }
    if (remaining <= 0) {
      onDone?.();
      return;
    }
    setTimeout(() => poll(remaining - 1), interval);
  };
  poll(retries);
}

function ServerDay(props) {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;
  const leadCount = highlightedDays.find(
    (item) => item.date === day.format("YYYY-MM-DD"),
  )?.count;
  return (
    <Badge
      badgeContent={leadCount || null}
      color="primary"
      overlap="circular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Badge>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState("");
  const [todayCount, setTodayCount] = useState(0);
  const [quotationCreatedCount, setQuotationCreatedCount] = useState(0);
  const [quotationsendCount, setQuotationSendCount] = useState(0);
  const [quotationReviseCount, setQuotationReviseCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [prospectCount, setProspectCount] = useState(0);
  const [wonCount, setWonCount] = useState(0);
  const [meetingCount, setMeetingCount] = useState(0);
  const [callbackCount, setCallbackCount] = useState(0);
  const [lostCount, setLostCount] = useState(0);
  const [globalTotalCount, setGlobalTotalCount] = useState(0);
  const [calendarOpenedOnce, setCalendarOpenedOnce] = useState(false);

  const [activeStatus, setActiveStatus] = useState("");
  const [activeDealStatus, setActiveDealStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);

  const [dateDealStatusFilter, setDateDealStatusFilter] = useState("");
  const [dealStatusDropdownOpen, setDealStatusDropdownOpen] = useState(false);
  const dealStatusDropdownRef = useRef(null);

  const activeStatusRef = useRef("");
  const activeDealStatusRef = useRef("");
  const selectedDateRef = useRef(dayjs());
  const selectedAgentRef = useRef(null);
  const leadsRef = useRef([]);
  const hasMoreRef = useRef(true);
  const todayCountRef = useRef(0);
  const totalItemsRef = useRef(0);
  const agentsRef = useRef([]);
  const meetingCountRef = useRef(0);
  const callbackCountRef = useRef(0);
  const wonCountRef = useRef(0);
  const lostCountRef = useRef(0);
  const prospectCountRef = useRef(0);
  const globalTotalCountRef = useRef(0);

  const dateDealStatusFilterRef = useRef("");
  dateDealStatusFilterRef.current = dateDealStatusFilter;
  activeStatusRef.current = activeStatus;
  activeDealStatusRef.current = activeDealStatus;
  selectedDateRef.current = selectedDate;
  selectedAgentRef.current = selectedAgent;
  leadsRef.current = leads;
  hasMoreRef.current = hasMore;
  todayCountRef.current = todayCount;
  totalItemsRef.current = totalItems;
  agentsRef.current = agents;
  meetingCountRef.current = meetingCount;
  callbackCountRef.current = callbackCount;
  wonCountRef.current = wonCount;
  lostCountRef.current = lostCount;
  prospectCountRef.current = prospectCount;
  globalTotalCountRef.current = globalTotalCount;

  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const agentDropdownRef = useRef(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const assignScrollLeadId = useRef(null);
  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  const isRestoringRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const pageRef = useRef(1);
  const observerRef = useRef(null);

  const getSnapshot = () => ({
    page: pageRef.current,
    hasMore: hasMoreRef.current,
    totalItems: totalItemsRef.current,
    todayCount: todayCountRef.current,
    activeStatus: activeStatusRef.current,
    activeDealStatus: activeDealStatusRef.current,
    selectedDate: selectedDateRef.current.toISOString(),
    selectedAgent: selectedAgentRef.current,
    agents: agentsRef.current,
    meetingCount: meetingCountRef.current,
    callbackCount: callbackCountRef.current,
    wonCount: wonCountRef.current,
    lostCount: lostCountRef.current,
    prospectCount: prospectCountRef.current,
    globalTotalCount: globalTotalCountRef.current,
    dateDealStatusFilter: dateDealStatusFilterRef.current,
  });

  const fetchPage = useCallback(
    async (
      pageNum,
      append,
      activeStatusVal,
      activeDealStatusVal,
      selectedDateVal,
      agentId,
    ) => {
      if (append) setIsFetchingMore(true);
      else setIsLoading(true);
      setError("");

      const dateStr = selectedDateVal.format("YYYY-MM-DD");
      const isToday = dateStr === dayjs().format("YYYY-MM-DD");

      try {
        const useStatusEndpoint =
          activeStatusVal === "pending" || !!activeDealStatusVal;

        if (useStatusEndpoint) {
          let res;
          if (activeStatusVal === "pending") {
            res = await fetchLeads({
              page: pageNum,
              status: "pending",
              limit: 10,
              agentId,
            });
          } else {
            res = await fetchLeadsByDealStatus(
              activeDealStatusVal,
              pageNum,
              10,
              agentId,
            );
          }
          if (res?.success) {
            const fetched = res.leads || [];
            const count = res.total || fetched.length;
            const tp = res.totalPages || 1;
            const newLeads = append
              ? [...leadsRef.current, ...fetched]
              : fetched;
            setLeads(newLeads);
            setTotalItems(count);
            setHasMore(pageNum < tp);
            pageRef.current = pageNum;
            saveCache({
              ...getSnapshot(),
              leads: newLeads,
              page: pageNum,
              hasMore: pageNum < tp,
              totalItems: count,
            });
          }
          return;
        }

        // Date-based fetch
        const res = await fetchLeadsByDate(dateStr, pageNum, agentId);
        if (res?.success) {
          const allFetched = res.todayLeads || [];
          if (!append && isToday) {
            setTodayCount(res.todayLeadCount || 0);
          }
          const dsFilter = dateDealStatusFilterRef.current;
          const fetched = dsFilter
            ? allFetched.filter((l) => l.deal_status === dsFilter)
            : allFetched;
          const count = dsFilter
            ? fetched.length
            : res.todayLeadCount || res.total || fetched.length;
          const tp = res.totalPages || 1;
          const newLeads = append ? [...leadsRef.current, ...fetched] : fetched;
          setLeads(newLeads);
          setTotalItems(count);
          setHasMore(pageNum < tp);
          pageRef.current = pageNum;
          saveCache({
            ...getSnapshot(),
            leads: newLeads,
            page: pageNum,
            hasMore: pageNum < tp,
            totalItems: count,
          });
        }
      } catch (err) {
        setError(err.message || "Failed to load leads");
      } finally {
        if (append) setIsFetchingMore(false);
        else setIsLoading(false);
        isLoadingMoreRef.current = false;
      }
    },
    [],
  );

  //  Restore scroll on back navigation
  useEffect(() => {
    const scrollRaw = sessionStorage.getItem(DASH_SCROLL_KEY);
    if (!scrollRaw) return;

    const cache = loadCache();
    if (!cache) return;

    const { leadId } = JSON.parse(scrollRaw);
    sessionStorage.removeItem(DASH_SCROLL_KEY);

    if (cache.activeStatus !== undefined) setActiveStatus(cache.activeStatus);
    if (cache.activeDealStatus !== undefined)
      setActiveDealStatus(cache.activeDealStatus);
    if (cache.selectedDate) setSelectedDate(dayjs(cache.selectedDate));
    if (cache.selectedAgent !== undefined)
      setSelectedAgent(cache.selectedAgent);
    if (cache.dateDealStatusFilter !== undefined)
      setDateDealStatusFilter(cache.dateDealStatusFilter);

    // Restore leads and pagination
    setLeads(cache.leads || []);
    pageRef.current = cache.page || 1;
    setHasMore(cache.hasMore ?? true);
    hasMoreRef.current = cache.hasMore ?? true;
    if (cache.totalItems !== undefined) setTotalItems(cache.totalItems);
    if (cache.todayCount !== undefined) setTodayCount(cache.todayCount);
    if (cache.meetingCount !== undefined) setMeetingCount(cache.meetingCount);
    if (cache.callbackCount !== undefined)
      setCallbackCount(cache.callbackCount);
    if (cache.wonCount !== undefined) setWonCount(cache.wonCount);
    if (cache.lostCount !== undefined) setLostCount(cache.lostCount);
    if (cache.prospectCount !== undefined)
      setProspectCount(cache.prospectCount);
    if (cache.globalTotalCount !== undefined)
      setGlobalTotalCount(cache.globalTotalCount);

    isRestoringRef.current = true;

    // Poll for the lead element
    pollAndScroll(`dash-lead-${leadId}`, () => {
      isRestoringRef.current = false;
    });
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [statsRes, repsRes, selfRes] = await Promise.all([
          fetchLeadStats(selectedAgent?.id ?? null),
          fetchUsersByRole("sales_person", { page: 1, limit: 100, search: "" }),
          getCurrentUser(),
        ]);
        const dealStats = statsRes.dealStatusStats || {};
        const meeting = dealStats.meeting || 0;
        const callback = dealStats.call_back || 0;
        const won = dealStats.won || 0;
        const lost = dealStats.lost || 0;
        const qc = dealStats.quotation_created || 0;
        const qs = dealStats.quotation_send || 0;
        const qr = dealStats.quotation_revised || 0;
        setGlobalTotalCount(statsRes.total || 0);
        setMeetingCount(meeting);
        setCallbackCount(callback);
        setWonCount(won);
        setLostCount(lost);
        setProspectCount(callback + meeting + qc + qs + qr);
        setQuotationCreatedCount(qc);
        setQuotationSendCount(qs);
        setQuotationReviseCount(qr);
        const users = repsRes?.users || repsRes?.data || [];
        const agentList = Array.isArray(users) ? users : [];
        const self = selfRes?.user;
        const selfEntry = self
          ? [{ id: self.id, name: `${self.name} (You)` }]
          : [];
        setAgents([
          ...selfEntry,
          ...agentList.filter((a) => a.id !== self?.id),
        ]);
      } catch (err) {
        console.error("Failed to load stats/agents", err);
      }
    };
    init();
  }, [selectedAgent]);

  // Fetch on filter change
  useEffect(() => {
    if (isRestoringRef.current) return;
    setLeads([]);
    setTotalItems(0);
    setHasMore(true);
    hasMoreRef.current = true;
    isLoadingMoreRef.current = false;
    pageRef.current = 1;
    fetchPage(
      1,
      false,
      activeStatus,
      activeDealStatus,
      selectedDate,
      selectedAgent?.id ?? null,
    );
  }, [
    activeStatus,
    activeDealStatus,
    selectedDate,
    selectedAgent,
    dateDealStatusFilter,
  ]);

  const sentinelCallbackRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (!hasMoreRef.current) return;
        if (isLoadingMoreRef.current) return;
        isLoadingMoreRef.current = true;
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        fetchPage(
          nextPage,
          true,
          activeStatusRef.current,
          activeDealStatusRef.current,
          selectedDateRef.current,
          selectedAgentRef.current?.id ?? null,
        );
      },
      { rootMargin: "300px 0px", threshold: 0 },
    );
    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    if (!calendarOpenedOnce || !openCalendar) return;
    const load = async () => {
      try {
        const monthStr = calendarMonth.startOf("month").format("YYYY-MM-DD");
        const data = await fetchLeadsByDate(
          monthStr,
          1,
          selectedAgent?.id ?? null,
        );
        setHighlightedDays(data?.futureLeadCounts || []);
      } catch {
        setHighlightedDays([]);
      }
    };
    load();
  }, [calendarMonth, selectedAgent, calendarOpenedOnce, openCalendar]);

  useEffect(() => {
    const close = (e) => {
      setContextMenu(null);
      if (
        agentDropdownRef.current &&
        !agentDropdownRef.current.contains(e.target)
      )
        setAgentDropdownOpen(false);
      if (
        dealStatusDropdownRef.current &&
        !dealStatusDropdownRef.current.contains(e.target)
      )
        setDealStatusDropdownOpen(false);
    };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const handleStatusClick = (value) => {
    setDateDealStatusFilter("");
    if (value === "pending") {
      setActiveStatus("pending");
      setActiveDealStatus("");
    } else {
      setActiveDealStatus(value);
      setActiveStatus("");
    }
  };

  const handleDateSelect = (dateObj) => {
    setSelectedDate(dateObj);
    setOpenCalendar(false);
    setActiveStatus("");
    setActiveDealStatus("");
    setDateDealStatusFilter("");
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setAgentDropdownOpen(false);
  };

  const handleLeadClick = (leadId) => {
    sessionStorage.setItem(DASH_SCROLL_KEY, JSON.stringify({ leadId }));
    saveCache({ ...getSnapshot(), leads: leadsRef.current });
    navigate(`/lead/${leadId}`);
  };

  const handleLeadAssigned = useCallback(
    (updatedLead) => {
      const leadId =
        assignScrollLeadId.current || updatedLead?.id || selectedLead?.id;
      if (!leadId) return;

      setLeads((prev) => {
        const next = prev.map((l) => {
          if (l.id !== leadId) return l;

          const resolvedAgentName =
            updatedLead?.agent_name ||
            agentsRef.current.find((a) => a.id === updatedLead?.agent_assigned)
              ?.name ||
            l.agent_name;

          return {
            ...l,
            agent_assigned: updatedLead?.agent_assigned ?? l.agent_assigned,
            agent_name: resolvedAgentName,
          };
        });

        const cache = loadCache();
        if (cache?.leads) saveCache({ ...cache, leads: next });

        return next;
      });

      requestAnimationFrame(() => {
        pollAndScroll(`dash-lead-${leadId}`);
      });
    },
    [selectedLead],
  );

  const handleAssignModalClose = useCallback((open) => {
    setIsAssignModalOpen(open);
    if (!open && assignScrollLeadId.current) {
      const id = assignScrollLeadId.current;
      assignScrollLeadId.current = null;
      setTimeout(() => pollAndScroll(`dash-lead-${id}`), 100);
    }
  }, []);

  const getActiveTitle = () => {
    const agentSuffix = selectedAgent ? ` · ${selectedAgent.name}` : "";
    const isToday =
      selectedDate.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
    const dateSuffix = isToday ? "" : ` · ${selectedDate.format("MMM D")}`;
    if (activeDealStatus) {
      const map = {
        meeting: "Meeting Arranged",
        call_back: "Call Scheduled",
        won: "Leads Won",
        lost: "Leads Lost",
        quotation_created: "Quotation Created",
        quotation_send: "Quotation Sent",
        quotation_revised: "Quotation Revised",
        new: "New Leads",
      };
      return {
        label: (map[activeDealStatus] || "Leads") + agentSuffix + dateSuffix,
        count: totalItems,
      };
    }
    if (activeStatus === "pending")
      return {
        label: "Pending Leads" + agentSuffix + dateSuffix,
        count: totalItems,
      };
    return {
      label: "Today's Leads" + agentSuffix + dateSuffix,
      count: totalItems,
    };
  };

  const { label, count } = getActiveTitle();

  return (
    <MobileLayout>
      <div className="p-4 space-y-6">
        {/* Header + Agent filter */}
        <DashboardHeader
          agentDropdownRef={agentDropdownRef}
          selectedAgent={selectedAgent}
          setAgentDropdownOpen={setAgentDropdownOpen}
          handleAgentSelect={handleAgentSelect}
          agentDropdownOpen={agentDropdownOpen}
          agents={agents}
        />

        {/* Stat Cards */}
        <DashboardStats
          activeStatus={activeStatus}
          activeDealStatus={activeDealStatus}
          selectedDate={selectedDate}
          setActiveStatus={setActiveStatus}
          setActiveDealStatus={setActiveDealStatus}
          setSelectedDate={setSelectedDate}
          todayCount={todayCount}
          handleStatusClick={handleStatusClick}
          meetingCount={meetingCount}
          callbackCount={callbackCount}
          wonCount={wonCount}
          prospectCount={prospectCount}
          lostCount={lostCount}
          quotationCreatedCount={quotationCreatedCount}
          quotationsendCount={quotationsendCount}
          quotationReviseCount={quotationReviseCount}
        />

        {/* Calendar Dialog */}
        <DashboardFilterCalendar
          openCalendar={openCalendar}
          setOpenCalendar={setOpenCalendar}
          selectedDate={selectedDate}
          handleDateSelect={handleDateSelect}
          setCalendarMonth={setCalendarMonth}
          ServerDay={ServerDay}
          highlightedDays={highlightedDays}
        />

        {/* Leads Section */}
        <DashboardLeadCard
          activeDealStatus={activeDealStatus}
          activeStatus={activeStatus}
          label={label}
          count={count}
          dealStatusDropdownRef={dealStatusDropdownRef}
          dateDealStatusFilter={dateDealStatusFilter}
          setDealStatusDropdownOpen={setDealStatusDropdownOpen}
          DEAL_STATUS_LABELS={DEAL_STATUS_LABELS}
          dealStatusDropdownOpen={dealStatusDropdownOpen}
          setDateDealStatusFilter={setDateDealStatusFilter}
          setCalendarOpenedOnce={setCalendarOpenedOnce}
          setOpenCalendar={setOpenCalendar}
          leads={leads}
          error={error}
          isLoading={isLoading}
          leadsRef={leadsRef}
          getSnapshot={getSnapshot}
          saveCache={saveCache}
          DASH_SCROLL_KEY={DASH_SCROLL_KEY}
          handleLeadClick={handleLeadClick}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          sentinelCallbackRef={sentinelCallbackRef}
          setIsAssignModalOpen={setIsAssignModalOpen}
          setSelectedLead={setSelectedLead}
          assignScrollLeadId={assignScrollLeadId}
        />
      </div>

      {isAssignModalOpen && (
        <EditLeadModal
          isOpen={isAssignModalOpen}
          setIsOpen={handleAssignModalClose}
          lead={selectedLead}
          assignOnly={true}
          onLeadUpdated={handleLeadAssigned}
        />
      )}
    </MobileLayout>
  );
}
